import { closeModalDialog, openModalDialog } from './interaction/ui/modal-dialog.js';

const content = document.getElementById('content');
const notice = document.getElementById('notice');
const tabs = document.querySelectorAll('.tab');
let activeTab = 'catalog';
/** 用户全部安装记录，含 role_code */
let installs = [];
/** 全站已发布目录 */
let catalog = [];
let roles = [];

function roleName(code) {
  const hit = roles.find((r) => r.code === code);
  return hit?.name || code || '默认角色';
}

function availableRoles() {
  if (roles.length) return roles;
  return [{ code: 'default', name: '默认角色' }];
}

function activateTab(tab) {
  if (!tab) return;
  activeTab = tab.dataset.tab;
  tabs.forEach((item) => {
    const selected = item === tab;
    item.classList.toggle('active', selected);
    item.setAttribute('aria-selected', String(selected));
    item.tabIndex = selected ? 0 : -1;
  });
  content?.setAttribute('aria-labelledby', tab.id);
  render();
}

tabs.forEach((tab, index) => {
  tab.addEventListener('click', () => activateTab(tab));
  tab.addEventListener('keydown', (event) => {
    let targetIndex = null;
    if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = tabs.length - 1;
    if (targetIndex === null) return;
    event.preventDefault();
    const target = tabs[targetIndex];
    activateTab(target);
    target.focus();
  });
});

async function api(path, opts = {}) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
}

function showError(msg) {
  if (!notice) return;
  const error = document.createElement('div');
  error.className = 'error';
  error.textContent = String(msg || '发生未知错误');
  notice.replaceChildren(error);
}

function clearError() {
  notice?.replaceChildren();
}

async function load() {
  try {
    const roleData = await api('/api/aichat/roles');
    roles = Array.isArray(roleData.roles) ? roleData.roles : [];
    // 不传 roleCode：catalog 全量 + installs 全量（已按用户隔离）
    const data = await api('/api/agent/skills');
    catalog = data.catalog || [];
    installs = Array.isArray(data.installs)
      ? data.installs
      : (Array.isArray(data.installed) ? data.installed : []);
    render();
  } catch (err) {
    showError(`加载失败：${err.message}`);
  }
}

function rolesHavingSkill(code) {
  return [...new Set(installs.filter((s) => s.code === code).map((s) => s.role_code).filter(Boolean))];
}

function parseArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function createSkillElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createRoleInstallDialog(skill, rolesList, installedRoles) {
  const modal = createSkillElement('div', 'skill-modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'skill-modal-title');
  const heading = createSkillElement('h3', '', `安装「${skill.name}」`);
  heading.id = 'skill-modal-title';
  const description = createSkillElement('p', 'skill-modal-desc', '选择要安装到的角色（可多选）。取消已勾选的角色将从该角色卸载。安装仅影响你自己的账号。');
  const rolesRoot = createSkillElement('div', 'skill-modal-roles');
  rolesList.forEach((role) => {
    const label = createSkillElement('label', 'skill-role-check');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.value = role.code;
    checkbox.checked = installedRoles.has(role.code);
    label.append(checkbox, createSkillElement('span', '', role.name || role.code));
    rolesRoot.append(label);
  });
  const actions = createSkillElement('div', 'skill-modal-actions');
  const cancel = createSkillElement('button', 'btn btn-secondary', '取消');
  cancel.type = 'button';
  cancel.dataset.modal = 'cancel';
  const confirm = createSkillElement('button', 'btn btn-primary', '确认');
  confirm.type = 'button';
  confirm.dataset.modal = 'ok';
  actions.append(cancel, confirm);
  modal.append(heading, description, rolesRoot, actions);
  return modal;
}

/** 安装角色选择弹层：可多选；已装角色默认勾选，取消勾选表示卸载 */
function openRoleInstallModal(skill) {
  return new Promise((resolve) => {
    const existing = document.getElementById('skill-role-modal');
    if (existing) {
      closeModalDialog(existing, { restoreFocus: false });
      existing.remove();
    }

    const onRoles = new Set(rolesHavingSkill(skill.code));
    const list = availableRoles();
    const overlay = document.createElement('div');
    overlay.id = 'skill-role-modal';
    overlay.className = 'skill-modal-overlay';
    overlay.append(createRoleInstallDialog(skill, list, onRoles));
    document.body.appendChild(overlay);

    const close = (result) => {
      closeModalDialog(overlay);
      overlay.remove();
      resolve(result);
    };
    openModalDialog(overlay, {
      onRequestClose: () => close(null),
    });
    overlay.querySelector('[data-modal="cancel"]')?.addEventListener('click', () => close(null));
    overlay.querySelector('[data-modal="ok"]')?.addEventListener('click', () => {
      const selected = [...overlay.querySelectorAll('input[type="checkbox"]:checked')].map((el) => el.value);
      close({ selected, previous: [...onRoles] });
    });
  });
}

async function applyRoleInstallSelection(skillCode, { selected, previous }) {
  const selectedSet = new Set(selected);
  const previousSet = new Set(previous);
  const toInstall = selected.filter((c) => !previousSet.has(c));
  const toUninstall = previous.filter((c) => !selectedSet.has(c));
  const errors = [];
  for (const roleCode of toInstall) {
    try {
      await api(`/api/agent/skills/${encodeURIComponent(skillCode)}/install?roleCode=${encodeURIComponent(roleCode)}`, { method: 'POST' });
    } catch (err) {
      errors.push(`安装到 ${roleName(roleCode)} 失败：${err.message}`);
    }
  }
  for (const roleCode of toUninstall) {
    try {
      await api(`/api/agent/skills/${encodeURIComponent(skillCode)}/uninstall?roleCode=${encodeURIComponent(roleCode)}`, { method: 'POST' });
    } catch (err) {
      errors.push(`从 ${roleName(roleCode)} 卸载失败：${err.message}`);
    }
  }
  if (errors.length) throw new Error(errors.join('；'));
  return { installed: toInstall.length, uninstalled: toUninstall.length };
}

function createSkillCard(s, { mode, roleCodeForUninstall }) {
  const tags = parseArray(s.tags);
  const tools = parseArray(s.tools);
  const desc = s.user_description || s.description || (s.prompt_template ? s.prompt_template.slice(0, 200) : '') || '';
  const onRoles = rolesHavingSkill(s.code);
  const card = createSkillElement('div', 'card');
  card.dataset.code = s.code || '';
  card.dataset.name = s.name || '';
  card.append(createSkillElement('h3', '', s.name || ''), createSkillElement('p', '', desc));
  const meta = createSkillElement('div', 'meta');
  const addTag = (value) => { if (value) meta.append(createSkillElement('span', 'tag', value)); };
  addTag(s.category);
  if (s.version) addTag(`v${s.version}`);
  tags.forEach(addTag);
  tools.forEach(addTag);
  const hint = createSkillElement('div', 'install-hint', onRoles.length
    ? `已安装到：${onRoles.map((code) => roleName(code)).join('、')}`
    : '尚未安装到任何角色');
  const actions = createSkillElement('div', 'actions');
  const button = createSkillElement('button', mode === 'catalog' ? 'btn btn-primary' : 'btn btn-secondary');
  button.type = 'button';
  if (mode === 'catalog') {
    button.dataset.action = 'manage-install';
    button.textContent = onRoles.length ? '管理安装角色' : '安装到角色…';
  } else {
    button.dataset.action = 'uninstall';
    button.dataset.role = roleCodeForUninstall || '';
    button.textContent = `从「${roleName(roleCodeForUninstall)}」卸载`;
  }
  actions.append(button);
  card.append(meta, hint, actions);
  return card;
}

function bindCardActions(root) {
  root.querySelectorAll('button[data-action]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const card = btn.closest('.card');
      const code = card.dataset.code;
      const name = card.dataset.name || code;
      const action = btn.dataset.action;
      btn.disabled = true;
      try {
        if (action === 'manage-install') {
          const skill = catalog.find((s) => s.code === code) || { code, name };
          const selection = await openRoleInstallModal(skill);
          if (!selection) {
            btn.disabled = false;
            return;
          }
          const result = await applyRoleInstallSelection(code, selection);
          if (result.installed === 0 && result.uninstalled === 0) {
            /* no-op */
          }
          await load();
          return;
        }
        if (action === 'uninstall') {
          const roleCode = btn.dataset.role || card.dataset.role || 'default';
          await api(`/api/agent/skills/${encodeURIComponent(code)}/uninstall?roleCode=${encodeURIComponent(roleCode)}`, { method: 'POST' });
          await load();
          return;
        }
      } catch (err) {
        showError(`操作失败：${err.message}`);
        btn.disabled = false;
      }
    });
  });
}

function renderCatalog() {
  const subtitle = createSkillElement('p', 'panel-sub', `展示全部已发布技能（共 ${catalog.length} 个）。点击「安装到角色」可多选角色；安装只影响你自己的账号，不同用户互不影响。`);
  if (catalog.length === 0) {
    content.replaceChildren(subtitle, createSkillElement('div', 'empty', '暂无已发布技能'));
    return;
  }
  const grid = createSkillElement('div', 'grid');
  catalog.forEach((skill) => grid.append(createSkillCard(skill, { mode: 'catalog' })));
  content.replaceChildren(subtitle, grid);
  bindCardActions(content);
}

function renderInstalledGrouped() {
  if (!installs.length) {
    content.replaceChildren(
      createSkillElement('p', 'panel-sub', '按角色分组查看已安装技能。'),
      createSkillElement('div', 'empty', '还没有安装任何技能'),
    );
    return;
  }

  const roleOrder = availableRoles().map((r) => r.code);
  const groups = new Map();
  for (const item of installs) {
    const rc = item.role_code || 'default';
    if (!groups.has(rc)) groups.set(rc, []);
    groups.get(rc).push(item);
  }
  const codes = [...groups.keys()].sort((a, b) => {
    const ia = roleOrder.indexOf(a);
    const ib = roleOrder.indexOf(b);
    if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const subtitle = createSkillElement('p', 'panel-sub');
  subtitle.append(document.createTextNode('已安装按'), createSkillElement('strong', '', '角色分组'), document.createTextNode(`（同一技能装到不同角色会分别列出）。共 ${installs.length} 条安装记录。`));
  const sections = codes.map((rc) => {
    const skills = groups.get(rc) || [];
    const section = createSkillElement('section', 'role-group');
    section.dataset.roleGroup = rc;
    const head = createSkillElement('div', 'role-group-head');
    head.append(createSkillElement('h2', '', roleName(rc)), createSkillElement('span', 'role-group-meta', `${skills.length} 个技能`));
    const grid = createSkillElement('div', 'grid');
    skills.forEach((skill) => grid.append(createSkillCard(skill, { mode: 'installed', roleCodeForUninstall: rc })));
    section.append(head, grid);
    return section;
  });

  content.replaceChildren(subtitle, ...sections);
  bindCardActions(content);
}

function render() {
  clearError();
  if (activeTab === 'installed') {
    renderInstalledGrouped();
  } else {
    renderCatalog();
  }
}

document.querySelector('[data-back-link]')?.addEventListener('click', (event) => {
  const referrer = document.referrer;
  if (!referrer) return;
  try {
    if (new URL(referrer).origin === window.location.origin) {
      event.preventDefault();
      window.history.back();
    }
  } catch { /* fallback href remains available */ }
});

load();
